// Types
import type { TPaginated } from '~/misc/resource/types';

enum EAutomationMode {
  OFF = 'off',
  SEMI_AUTOMATIC = 'semi-automated',
  FULLY_AUTOMATIC = 'fully-automated',
}

type TAutomatedEmailSaleType = 'sale' | 'let';

type TAutomationStatus = {
  mode: EAutomationMode;
  is_enabled: boolean;
  contact_frequency: number;
  semi_automatic_auto_send_days?: number;
  sender_user_id: number | null;
  has_changed_today: boolean;
};

type TAutomationStatusPayload = {
  mode: EAutomationMode;
  is_enabled: boolean;
  contact_frequency: number;
  semi_automatic_auto_send_days: number;
};

type TAutomatedEmailUserSettingsResponse = {
  is_enabled: boolean;
  mode: EAutomationMode;
  contact_frequency: number;
  send_delay_hours: number | null;
  sender_user_id: number | null;
  updated_at?: string | null;
};

type TAutomatedEmailUserSettingsUpdatePayload = {
  patch_id: number;
  sale_type: TAutomatedEmailSaleType;
  sender_user_id: number;
  is_enabled?: boolean;
  mode?: EAutomationMode;
  contact_frequency?: number;
  send_delay_hours?: number | null;
};

type TAutomatedEmailPatchSender = {
  id: number;
  name: string;
  email: string;
  domain_verified: boolean;
};

type TAutomatedEmailUserTriggerApi = {
  id: number;
  trigger_name: string;
  is_active: boolean;
};

type TAutomatedEmailUserTriggerSyncRow = {
  trigger_name: string;
  is_active: boolean;
};

type TAutomatedEmailUserTriggerSyncPayload = {
  patch_id: number;
  sale_type: TAutomatedEmailSaleType;
  triggers: TAutomatedEmailUserTriggerSyncRow[];
};

type TAutomatedEmailUserTemplateApi = {
  id: number;
  patch_trigger_id: number;
  subject: string;
  content: string;
  is_active: boolean;
};

type TAutomatedEmailUserTemplateStoreRow = {
  patch_trigger_id: number;
  subject: string;
  content: string;
  is_active: boolean;
};

type TAutomatedEmailTemplateActivateRow = {
  id: number;
  value: boolean;
};

type TAutomatedEmailDefaultTemplateSeed = {
  subject: string;
  content: string;
};

type TAutomatedEmailDefaultTemplatesResponse = Record<string, TAutomatedEmailDefaultTemplateSeed[]>;

type TEmailTemplate = {
  id: number;
  title: string;
  content: string;
  is_linked: boolean;
};

type TTrigger = {
  id: number;
  is_active: boolean;
  name: string;
  description: string;
  templates: TEmailTemplate[];
};

type TTriggerPayload = {
  is_active: boolean;
};

type TTemplatePayload = {
  id: number;
  is_active: boolean;
};

type TAutomatedEmailOutboxApiRow = {
  id: number;
  opportunity_id: number;
  contact_id?: number | null;
  contact_name: string;
  contact_email: string | null;
  listing_id: number;
  address_id?: number | null;
  listing_beds?: number | null;
  listing_category?: string | null;
  listing_price?: number | null;
  listing_status_id?: number | null;
  listing_address?: string | null;
  opportunity_property_beds?: number | null;
  opportunity_property_category?: string | null;
  opportunity_property_price?: number | null;
  opportunity_property_address?: string | null;
  patch_id: number;
  sale_type: string;
  property_address?: string;
  trigger_name: string;
  subject: string;
  content: string;
  send_at: string | null;
  created_at: string;
  status?: string;
};

type TOutboxEmail = {
  id: number;
  contact_name: string | null | undefined;
  property_address: string;
  trigger: string;
  trigger_name?: string;
  subject: string;
  content: string;
  contact_email?: string | null;
  opportunity_id?: number | null;
  contact_id?: number | null;
  listing_id?: number | null;
  address_id?: number | null;
  listing_beds?: number | null;
  listing_category?: string | null;
  listing_price?: number | null;
  listing_status_id?: number | null;
  listing_address?: string | null;
  opportunity_property_beds?: number | null;
  opportunity_property_category?: string | null;
  opportunity_property_price?: number | null;
  opportunity_property_address?: string | null;
  patch_id?: number | null;
  sale_type?: string | null;
  created_at?: string;
  send_at?: string | null;
  status?: string;
};

type TAutomatedEmailSentApiRow = {
  id: number;
  opportunity_id: number | null;
  contact_id: number | null;
  contact_name: string;
  contact_email: string;
  listing_id: number;
  patch_id: number;
  sale_type: string;
  property_address: string;
  trigger_name: string;
  subject: string;
  content: string;
  mode: string;
  sent_at: string;
};

type TSentEmail = {
  id: number;
  contact_name: string;
  trigger: string;
  trigger_name?: string;
  subject: string;
  content: string;
  date_sent: string;
  mode: string;
  contact_email?: string;
  property_address?: string;
  opportunity_id?: number | null;
  contact_id?: number | null;
  listing_id?: number | null;
  patch_id?: number | null;
  sale_type?: string | null;
};

type TPaginatedSentEmails = TPaginated<TSentEmail>;

type TAutomatedEmailDashboardApiResponse = {
  contacts_monitored: number;
  emails_sent: number;
  outbox_pending: number;
  is_fully_automated: boolean;
};

type TDashboardStats = {
  contacts_monitored: number;
  emails_sent: number;
  outbox_pending: number;
  is_automated: boolean;
};

type TAutomatedEmailDomainStatus = {
  id: number;
  domain: string;
  state: string;
  created_at: string;
};

type TAutomatedEmailSentListQuery = Record<string, string | number> & {
  patch_id: number;
  sale_type: TAutomatedEmailSaleType;
};

export { EAutomationMode };
export type {
  TAutomatedEmailSaleType,
  TAutomationStatus,
  TAutomationStatusPayload,
  TAutomatedEmailOutboxApiRow,
  TAutomatedEmailSentApiRow,
  TAutomatedEmailSentListQuery,
  TAutomatedEmailDashboardApiResponse,
  TAutomatedEmailDefaultTemplateSeed,
  TAutomatedEmailDefaultTemplatesResponse,
  TAutomatedEmailTemplateActivateRow,
  TAutomatedEmailPatchSender,
  TAutomatedEmailUserSettingsResponse,
  TAutomatedEmailUserSettingsUpdatePayload,
  TAutomatedEmailUserTemplateApi,
  TAutomatedEmailUserTemplateStoreRow,
  TAutomatedEmailUserTriggerApi,
  TAutomatedEmailUserTriggerSyncPayload,
  TAutomatedEmailUserTriggerSyncRow,
  TDashboardStats,
  TAutomatedEmailDomainStatus,
  TEmailTemplate,
  TOutboxEmail,
  TPaginatedSentEmails,
  TSentEmail,
  TTemplatePayload,
  TTrigger,
  TTriggerPayload,
};
